/**
 * Soft duplicate nudge for Invoices & Credit Notes upload review (Chunk U4).
 * Same primary gate as GR: supplier + normalised invoice number.
 */

import { normaliseInvoiceNumberForMultiDoc } from './invoiceMultiDocRules'

function normaliseId(value) {
  return String(value || '').trim()
}

function suppliersMatch(file = {}, invoice = {}) {
  const fileId = normaliseId(file.supplier || file.supplierId)
  const invId = normaliseId(
    invoice.supplier?._id || invoice.supplier || invoice.supplierId,
  )
  if (fileId && invId) return fileId === invId

  const fileTitle = String(file.supplierTitle || '')
    .trim()
    .toLowerCase()
  const invTitle = String(
    invoice.supplierTitle || invoice.supplier?.title || '',
  )
    .trim()
    .toLowerCase()
  if (fileTitle && invTitle) return fileTitle === invTitle
  return false
}

/**
 * @returns {null | {
 *   uploadFile: object,
 *   existingInvoice: object,
 *   invoiceNumber: string,
 *   existingInvoiceId: string,
 * }}
 */
export function findStrongLibraryDuplicateForUpload({
  uploadFiles = [],
  libraryInvoices = [],
  excludeInvoiceIds = [],
} = {}) {
  const files = Array.isArray(uploadFiles) ? uploadFiles : []
  const library = Array.isArray(libraryInvoices) ? libraryInvoices : []
  const exclude = new Set(
    (excludeInvoiceIds || []).map(id => String(id)).filter(Boolean),
  )

  for (const file of files) {
    if (String(file.documentType || '') === 'creditNote') continue
    const invoiceNumber = String(file.invoiceNumber || '').trim()
    const normalised = normaliseInvoiceNumberForMultiDoc(invoiceNumber)
    if (!normalised) continue

    const selfId = normaliseId(file.invoiceId)
    const matches = library.filter(invoice => {
      const id = normaliseId(invoice._id || invoice.id)
      if (!id || exclude.has(id) || (selfId && id === selfId)) return false
      if (invoice.isArchived) return false
      if (!suppliersMatch(file, invoice)) return false
      const existingNumber = normaliseInvoiceNumberForMultiDoc(
        invoice.invoiceNumber,
      )
      return Boolean(existingNumber) && existingNumber === normalised
    })

    if (!matches.length) continue

    const preferred = matches[0]
    return {
      uploadFile: file,
      existingInvoice: preferred,
      invoiceNumber: preferred.invoiceNumber || invoiceNumber || normalised,
      existingInvoiceId: String(preferred._id || preferred.id || ''),
    }
  }

  return null
}

export const UPLOAD_DUPLICATE_COPY = {
  title: 'Possible duplicate invoice',
  body: (invoiceNumber, supplierTitle) =>
    `An invoice with number ${
      invoiceNumber ? `#${invoiceNumber}` : 'this number'
    } is already in Invoices & Credit Notes${
      supplierTitle ? ` for ${supplierTitle}` : ''
    }. Using the existing file avoids a duplicate.`,
  continueOk: 'Continue anyway',
  useExisting: 'Use existing instead',
}
